import { useState, type FormEvent } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import type { Brand } from "../../../../shared/types";

interface BrandProfileProps {
  brand: Brand;
  onSave: (data: Partial<Brand>) => void;
  saving?: boolean;
}

export function BrandProfile({ brand, onSave, saving }: BrandProfileProps) {
  const [name, setName] = useState(brand.name);
  const [industry, setIndustry] = useState(brand.industry);
  const [targetAudience, setTargetAudience] = useState(brand.target_audience);
  const [websiteUrl, setWebsiteUrl] = useState(brand.website_url || "");
  const [toneInput, setToneInput] = useState("");
  const [tones, setTones] = useState<string[]>(brand.tone_descriptors);

  function handleAddTone() {
    if (toneInput.trim() && !tones.includes(toneInput.trim())) {
      setTones([...tones, toneInput.trim()]);
      setToneInput("");
    }
  }

  function handleRemoveTone(tone: string) {
    setTones(tones.filter((t) => t !== tone));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onSave({
      name,
      industry,
      target_audience: targetAudience,
      website_url: websiteUrl || null,
      tone_descriptors: tones,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input label="Brand name" value={name} onChange={(e) => setName(e.target.value)} required />
      <Input label="Industry" value={industry} onChange={(e) => setIndustry(e.target.value)} required />
      <Input
        label="Target audience"
        value={targetAudience}
        onChange={(e) => setTargetAudience(e.target.value)}
        placeholder="e.g. Solo founders building with AI"
        required
      />
      <Input
        label="Website URL"
        type="url"
        value={websiteUrl}
        onChange={(e) => setWebsiteUrl(e.target.value)}
        placeholder="https://yoursite.com"
      />

      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Tone descriptors</label>
        <div className="mb-2 flex flex-wrap gap-2">
          {tones.map((tone) => (
            <span
              key={tone}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-700"
            >
              {tone}
              <button
                type="button"
                onClick={() => handleRemoveTone(tone)}
                className="ml-1 text-blue-400 hover:text-blue-600"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={toneInput}
            onChange={(e) => setToneInput(e.target.value)}
            placeholder="Add tone (e.g. confident, casual)"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddTone();
              }
            }}
          />
          <Button type="button" variant="secondary" onClick={handleAddTone}>
            Add
          </Button>
        </div>
      </div>

      <Button type="submit" disabled={saving}>
        {saving ? "Saving..." : "Save profile"}
      </Button>
    </form>
  );
}
